import { Context } from '../context/Context';
import { Camera } from '../camera';
import { FullscreenQuad } from './FullscreenQuad';
import { GBuffer } from './GBuffer';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';
import { DirectionalLight } from '../light';
import { mat4, vec3, vec4 } from 'gl-matrix';

export class GodRaysPass {
  private occlusionFBO!: WebGLFramebuffer;
  private blurFBO!: WebGLFramebuffer;
  private occlusionTexture!: RenderTexture;
  private blurTexture!: RenderTexture;
  private width!: number;
  private height!: number;

  private resolutionDivisor = 4;

  init(ctx: Context, resolutionDivisor: number = 4): void {
    const gl = ctx.gl;
    this.resolutionDivisor = resolutionDivisor;
    this.width = Math.max(1, Math.floor(ctx.viewPort.width / this.resolutionDivisor));
    this.height = Math.max(1, Math.floor(ctx.viewPort.height / this.resolutionDivisor));

    this.occlusionTexture = new RenderTexture();
    this.occlusionTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA8,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
    );
    this.occlusionFBO = createFramebuffer(gl, this.occlusionTexture.glTexture!);

    this.blurTexture = new RenderTexture();
    this.blurTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA8,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
    );
    this.blurFBO = createFramebuffer(gl, this.blurTexture.glTexture!);
  }

  setResolutionDivisor(ctx: Context, divisor: number): void {
    if (divisor === this.resolutionDivisor) return;
    this.resolutionDivisor = divisor;
    const gl = ctx.gl;
    if (this.occlusionFBO) gl.deleteFramebuffer(this.occlusionFBO);
    if (this.blurFBO) gl.deleteFramebuffer(this.blurFBO);
    if (this.occlusionTexture?.glTexture) gl.deleteTexture(this.occlusionTexture.glTexture);
    if (this.blurTexture?.glTexture) gl.deleteTexture(this.blurTexture.glTexture);

    this.width = Math.max(1, Math.floor(ctx.viewPort.width / this.resolutionDivisor));
    this.height = Math.max(1, Math.floor(ctx.viewPort.height / this.resolutionDivisor));

    this.occlusionTexture = new RenderTexture();
    this.occlusionTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA8,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
    );
    this.occlusionFBO = createFramebuffer(gl, this.occlusionTexture.glTexture!);

    this.blurTexture = new RenderTexture();
    this.blurTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA8,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
    );
    this.blurFBO = createFramebuffer(gl, this.blurTexture.glTexture!);
  }

  render(
    ctx: Context,
    gBuffer: GBuffer,
    quad: FullscreenQuad,
    camera: Camera,
    sunLight: DirectionalLight,
    outputBuffer: WebGLFramebuffer | null = null,
  ): void {
    const gl = ctx.gl;

    const { pos: sunScreenPos, visibility: sunVisibility } = this.computeSunScreenPos(camera, sunLight);
    if (sunVisibility <= 0) return;

    // === 1. Occlusion pass ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.occlusionFBO);
    gl.viewport(0, 0, this.width, this.height);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const occlusionShader = ctx.engine.shaderManager.getGodRaysOcclusionShader();
    ctx.setShader(occlusionShader);

    occlusionShader.uniformsContainer
      .setDepthMap(gBuffer.depthTexture)
      .setSunColor(sunLight.color as number[]);

    occlusionShader.validate();
    quad.draw(gl);

    // === 2. Radial blur pass ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.blurFBO);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const blurShader = ctx.engine.shaderManager.getGodRaysBlurShader();
    ctx.setShader(blurShader);

    blurShader.uniformsContainer
      .setOcclusionMap(this.occlusionTexture)
      .setDepthMap(gBuffer.depthTexture)
      .setSunScreenPos(sunScreenPos)
      .setTexelSize([1.0 / this.width, 1.0 / this.height])
      .setDensity(0.8)
      .setWeight(0.012)
      .setDecay(0.94)
      .setExposure(sunVisibility);

    blurShader.validate();
    quad.draw(gl);

    // === 3. Composite onto screen (additive blend) ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.disable(gl.DEPTH_TEST);

    const compositeShader = ctx.engine.shaderManager.getFullscreenCompositeShader();
    ctx.setShader(compositeShader);
    compositeShader.uniformsContainer.setTextureSampler(this.blurTexture);

    compositeShader.validate();
    quad.draw(gl);

    gl.disable(gl.BLEND);
  }

  private computeSunScreenPos(
    camera: Camera,
    sunLight: DirectionalLight,
  ): { pos: [number, number]; visibility: number } {
    const sunDir = vec3.normalize(vec3.create(), sunLight.direction as vec3);
    const farPoint = vec3.scale(vec3.create(), sunDir, -500);
    vec3.add(farPoint, farPoint, camera.cameraPosition as vec3);

    const vp = mat4.mul(mat4.create(), camera.projectionMatrix, camera.modelViewMatrix);
    const clip = vec4.transformMat4(
      vec4.create(),
      vec4.fromValues(farPoint[0], farPoint[1], farPoint[2], 1),
      vp,
    );

    // Sun behind camera: perspective divide would flip, producing wrong-direction streaks
    if (clip[3] <= 0) return { pos: [0.5, 0.5], visibility: 0 };

    // Perspective divide → NDC → UV
    const x = (clip[0] / clip[3]) * 0.5 + 0.5;
    const y = (clip[1] / clip[3]) * 0.5 + 0.5;

    // Fade to 0 as sun moves off-screen; fadeMargin = distance past edge at which effect is fully gone
    const fadeMargin = 0.4;
    const ox = x < 0 ? -x : x > 1 ? x - 1 : 0;
    const oy = y < 0 ? -y : y > 1 ? y - 1 : 0;
    const dist = Math.sqrt(ox * ox + oy * oy);
    const visibility = Math.max(0, 1 - dist / fadeMargin);

    return { pos: [x, y], visibility };
  }

  destroy(gl: WebGL2RenderingContext): void {
    gl.deleteFramebuffer(this.occlusionFBO);
    gl.deleteFramebuffer(this.blurFBO);
    if (this.occlusionTexture.glTexture) gl.deleteTexture(this.occlusionTexture.glTexture);
    if (this.blurTexture.glTexture) gl.deleteTexture(this.blurTexture.glTexture);
  }
}
